import React, { useState } from "react";
import axios from "axios";
import { Phone, LogOut, Shield, Loader2, CheckCircle2, AlertCircle, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AccountSecurityCard — reusable security panel for OTP users (iter171).
 * Exposes two actions:
 *   • Change Phone Number (3-step OTP → OTP → confirm)
 *   • Sign out of all devices (rotate session)
 *
 * Props:
 *   authAxios — authenticated axios instance (cookies + X-*-Auth headers)
 *   onSessionRotated — callback (parent should force a login redirect)
 *   label — optional heading override
 */
const AccountSecurityCard = ({ authAxios, onSessionRotated, label = "Account Security" }) => {
  const api = authAxios || axios;
  const [mode, setMode] = useState("idle"); // idle | request_old | enter_new_phone | verify_new | success
  const [oldOtp, setOldOtp] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newOtp, setNewOtp] = useState("");
  const [changeToken, setChangeToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const reset = () => {
    setMode("idle"); setOldOtp(""); setNewPhone(""); setNewOtp("");
    setChangeToken(""); setError(""); setInfo(""); setBusy(false);
  };

  const requestOldOtp = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`${API_URL}/api/account/request-phone-change-otp`, {});
      setInfo(`OTP sent to your current phone ${r.data.phone}`);
      setMode("enter_new_phone");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not send OTP.");
    }
    setBusy(false);
  };

  const verifyOldAndSendNew = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`${API_URL}/api/account/verify-phone-change`, {
        old_otp: oldOtp, new_phone: newPhone,
      });
      setChangeToken(r.data.change_token);
      setInfo(`OTP sent to new number ${r.data.new_phone}`);
      setMode("verify_new");
    } catch (e) {
      setError(e.response?.data?.detail || "Could not verify OTP.");
    }
    setBusy(false);
  };

  const confirmNewOtp = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`${API_URL}/api/account/confirm-phone-change`, {
        change_token: changeToken, new_otp: newOtp,
      });
      setInfo(r.data.message);
      setMode("success");
      setTimeout(() => { onSessionRotated && onSessionRotated(); }, 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not confirm OTP.");
    }
    setBusy(false);
  };

  const signOutEverywhere = async () => {
    if (!window.confirm("Sign out of ALL devices now? You will be logged out everywhere.")) return;
    setBusy(true); setError("");
    try {
      const r = await api.post(`${API_URL}/api/account/rotate-session`, {});
      setInfo(r.data.message);
      setTimeout(() => { onSessionRotated && onSessionRotated(); }, 1500);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not sign out.");
    }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5" data-testid="account-security-card">
      <div className="flex items-center gap-2">
        <Shield className="text-[#E83E8C]" size={18} />
        <h3 className="font-['Outfit'] text-base font-bold text-[#1E1B4B]">{label}</h3>
      </div>

      {(error || info) && (
        <div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${error ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="account-security-banner">
          {error ? <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> : <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" />}
          <span>{error || info}</span>
          <button onClick={() => { setError(""); setInfo(""); }} className="ml-auto text-current/70 hover:text-current"><X size={12} /></button>
        </div>
      )}

      {/* Phone change */}
      <div className="space-y-2.5">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1E1B4B]">
          <Phone size={14} /> Change phone number
        </div>
        {mode === "idle" && (
          <button onClick={requestOldOtp} disabled={busy} className="px-3 py-1.5 rounded-full bg-[#1E1B4B] text-white text-xs font-bold disabled:opacity-60 inline-flex items-center gap-1.5"
            data-testid="phone-change-start">
            {busy && <Loader2 className="animate-spin" size={11} />} Start phone change
          </button>
        )}
        {mode === "enter_new_phone" && (
          <div className="space-y-2">
            <input type="text" inputMode="numeric" placeholder="6-digit OTP sent to current phone" value={oldOtp} onChange={e => setOldOtp(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="phone-change-old-otp" />
            <input type="tel" placeholder="New phone (10-digit)" value={newPhone} onChange={e => setNewPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="phone-change-new-phone" />
            <div className="flex gap-2">
              <button onClick={verifyOldAndSendNew} disabled={busy || !oldOtp || !newPhone}
                className="px-3 py-1.5 rounded-full bg-[#E83E8C] text-white text-xs font-bold disabled:opacity-60"
                data-testid="phone-change-verify-old">
                {busy ? "Sending…" : "Send OTP to new phone"}
              </button>
              <button onClick={reset} className="px-3 py-1.5 rounded-full text-xs text-gray-500">Cancel</button>
            </div>
          </div>
        )}
        {mode === "verify_new" && (
          <div className="space-y-2">
            <input type="text" inputMode="numeric" placeholder="6-digit OTP sent to new phone" value={newOtp} onChange={e => setNewOtp(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="phone-change-new-otp" />
            <div className="flex gap-2">
              <button onClick={confirmNewOtp} disabled={busy || !newOtp}
                className="px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-bold disabled:opacity-60"
                data-testid="phone-change-confirm">
                {busy ? "Confirming…" : "Confirm & switch"}
              </button>
              <button onClick={reset} className="px-3 py-1.5 rounded-full text-xs text-gray-500">Cancel</button>
            </div>
          </div>
        )}
        {mode === "success" && (
          <p className="text-xs text-emerald-700" data-testid="phone-change-success">Phone updated. Redirecting to login…</p>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4 space-y-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1E1B4B]">
          <LogOut size={14} /> Sign out everywhere
        </div>
        <p className="text-[11px] text-gray-500">
          Suspect your account is being used on another device? Rotate your session —
          every other device will be logged out immediately.
        </p>
        <button onClick={signOutEverywhere} disabled={busy}
          className="px-3 py-1.5 rounded-full bg-rose-500 text-white text-xs font-bold disabled:opacity-60"
          data-testid="rotate-session-btn">
          Sign out of all devices
        </button>
      </div>
    </div>
  );
};

export default AccountSecurityCard;
